import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Edit3 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

type Memory = {
  id: string;
  user_id: string;
  memory_text: string;
  memory_kind: string | null;
  source_chat_id: string | null;
  embedding_model: string | null;
  is_active: boolean | null;
  last_used_at: string | null;
  use_count: number | null;
  created_at: string;
  updated_at: string;
};

const KINDS = ["preference", "fact", "constraint", "style", "context", "custom"];

export default function ResearchMemory() {
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Memory | null>(null);
  const [creating, setCreating] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftKind, setDraftKind] = useState("preference");
  const qc = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ["auth_user"],
    queryFn: async () => (await supabase.auth.getUser()).data.user,
  });

  const { data: memories, isLoading } = useQuery({
    queryKey: ["research_user_memory", scope, me?.id],
    queryFn: async () => {
      let query = (supabase as any)
        .from("research_user_memory")
        .select("id,user_id,memory_text,memory_kind,source_chat_id,embedding_model,is_active,last_used_at,use_count,created_at,updated_at")
        .order("updated_at", { ascending: false })
        .limit(500);
      if (scope === "mine" && me?.id) query = query.eq("user_id", me.id);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Memory[];
    },
    enabled: scope === "all" || !!me?.id,
  });

  const filtered = useMemo(() => {
    let list = memories ?? [];
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((m) => m.memory_text.toLowerCase().includes(n) || (m.memory_kind ?? "").toLowerCase().includes(n));
    }
    return list;
  }, [memories, q]);

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase as any).from("research_user_memory").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["research_user_memory"] }),
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("research_user_memory").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Memory deleted");
      qc.invalidateQueries({ queryKey: ["research_user_memory"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!draftText.trim()) throw new Error("Memory text required");
      if (editing) {
        const { error } = await (supabase as any)
          .from("research_user_memory")
          .update({ memory_text: draftText, memory_kind: draftKind })
          .eq("id", editing.id);
        if (error) throw error;
      } else {
        if (!me?.id) throw new Error("Not signed in");
        const { error } = await (supabase as any).from("research_user_memory").insert({
          user_id: me.id,
          memory_text: draftText,
          memory_kind: draftKind,
          is_active: true,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editing ? "Memory updated" : "Memory created");
      setEditing(null); setCreating(false); setDraftText(""); setDraftKind("preference");
      qc.invalidateQueries({ queryKey: ["research_user_memory"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const openEdit = (m: Memory) => {
    setEditing(m); setCreating(false);
    setDraftText(m.memory_text);
    setDraftKind(m.memory_kind ?? "preference");
  };
  const openCreate = () => {
    setCreating(true); setEditing(null);
    setDraftText(""); setDraftKind("preference");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">User memory</h2>
          <p className="text-sm text-muted-foreground">
            Per-user memories injected into the research assistant. Inactive rows are skipped at retrieval time. Embeddings are generated on the next chat turn.
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" />New memory</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={(v) => setScope(v as any)}>
          <TabsList>
            <TabsTrigger value="mine">Mine</TabsTrigger>
            <TabsTrigger value="all">All users</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          placeholder="Filter by text or kind…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">{filtered.length} memor{filtered.length === 1 ? "y" : "ies"}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Kind</TableHead>
                <TableHead>Memory</TableHead>
                <TableHead className="w-24">Used</TableHead>
                <TableHead className="w-32">Updated</TableHead>
                <TableHead className="w-20">Active</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No entries</TableCell></TableRow>
              )}
              {filtered.map((m) => (
                <TableRow key={m.id}>
                  <TableCell><Badge variant="outline" className="text-[10px]">{m.memory_kind ?? "—"}</Badge></TableCell>
                  <TableCell className="text-xs">
                    <div className="line-clamp-3">{m.memory_text}</div>
                    {!m.embedding_model && (
                      <div className="text-[10px] text-yellow-700 mt-0.5">no embedding yet</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {m.use_count ?? 0}×
                    {m.last_used_at && <div className="text-[10px]">{format(new Date(m.last_used_at), "dd/MM/yyyy")}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{format(new Date(m.updated_at), "dd/MM/yyyy HH:mm")}</TableCell>
                  <TableCell>
                    <Switch checked={!!m.is_active} onCheckedChange={(c) => toggleActive.mutate({ id: m.id, is_active: c })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Edit3 className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete this memory?")) remove.mutate(m.id); }}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit memory" : "New memory"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium">Kind</label>
              <Select value={draftKind} onValueChange={setDraftKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Memory text</label>
              <Textarea
                rows={5}
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="e.g. 'Always cite the FRED series ID alongside any macro figure', 'I focus on P3/P4 portfolios', 'Prefer concise bullet summaries'"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setCreating(false); }}>Cancel</Button>
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
